// @ts-nocheck
/**
 * PW Sync Utilities - Shared helpers for authentication sync
 */

/* -------------------- Cookie & Storage Helpers -------------------- */

export function getCookie(name) {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  return parts.length === 2 ? parts.pop().split(";").shift() : null;
}

export function safeJSONParse(value) {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

export function toBoolean(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "boolean") return value;
  return value === "true";
}

/* -------------------- Auth State Helpers -------------------- */

export function getFromAppFlag() {
  const params = new URLSearchParams(window.location.search);
  return toBoolean(
    params.get("from_app") ??
    params.get("fromApp") ??
    params.get("from_App") ??
    sessionStorage.getItem("fromApp")
  );
}

export function getForceLogoutFlag() {
  return toBoolean(
    sessionStorage.getItem("force_logout") ??
    localStorage.getItem("force_logout")
  ) || false;
}

/* -------------------- Cleanup & Logout -------------------- */

export function forceLogout(reason = "unknown") {
  console.error("[PW Sync] Forcing logout:", reason);

  // Local storage cleanup
  localStorage.removeItem("STORE_TOKEN_CONTEXT");
  localStorage.removeItem("preferences");
  localStorage.removeItem("pw-sync-token");
  localStorage.removeItem("pw-sync-random-id");
  localStorage.removeItem("selectedExamName");
  localStorage.removeItem("selectedExamIcon");
  localStorage.removeItem("selectedExamRedirectUrl");
  
  // Keep auth failed flag during logout to prevent retry, 
  // but set a flag to clear it after logout completes
  sessionStorage.setItem("pw_logout_in_progress", "true");

  // CookieStore API (SAFE)
  if ("cookieStore" in window) {
    cookieStore.delete("STORE_TOKEN_CONTEXT");
    cookieStore.delete("preferences");
  }

  // Redirect AFTER cleanup
  window.location.replace("/account/logout");  
}

export function removeSyncingState(options = {}) {
  const { shouldRedirect = false, redirectUrl = "/" } = options;

  localStorage.removeItem('pw-sync-token');
  localStorage.removeItem('pw-sync-random-id');

  const isAuthenticatePage = window.location.pathname === "/pages/authenticate";
  if (!isAuthenticatePage) {
    const loaderSection = document.querySelector('.loader-section');
    if (loaderSection) {
      loaderSection.classList.add('fade-out');
      setTimeout(() => {
        loaderSection.style.display = 'none';
      }, 300);
    }
  }

  if (shouldRedirect) {
    window.location.replace(redirectUrl);
  } 
}

/* -------------------- API Sync -------------------- */

export async function syncWithLiveTokenContext({ token, randomId, redirectUrl }) {
  if (!token || !randomId) return;
  console.log("[PW Sync] Sending redirectUrl to backend:", redirectUrl);
  // Add timeout to prevent hanging
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

  try {
    const response = await fetch("/apps/pw_module_staging/api/auth/multipass", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        randomId,
        redirectUrl,
        fromApp: getFromAppFlag(),
      }),
      signal: controller.signal,
    });

    clearTimeout(timeoutId);
    
    // Handle 401/403 specifically to prevent infinite loop
    if (response.status === 401 || response.status === 403  || response.status === 400) {
      console.error("[PW Sync] Authentication failed:", response.status);
      sessionStorage.setItem('pw_auth_failed', 'true');
      forceLogout('Authentication failed: ' + response.status);
      return;
    }

    const data = await response.json();
    console.log("[PW Sync] Backend redirect_url:", data?.data?.redirect_url);

    if (!response.ok || data.status !== "success") {
      throw new Error("Multipass sync failed with status: " + response.status);
    }

    /* -------- Success handling -------- */

    // Clear any previous auth failure flags
    sessionStorage.removeItem('pw_auth_failed');

    localStorage.setItem(
      "STORE_TOKEN_CONTEXT",
      JSON.stringify({ token, randomId })
    );

    sessionStorage.removeItem("force_logout");

    // Update last sync time
    sessionStorage.setItem('pw_last_sync_time', Date.now().toString());

    const preferences = data?.data?.preferences;
    const exams = preferences?.exams;
    const cohort = exams?.[exams.length - 1];
    const selectedExamName = cohort ? String(cohort).split("-").join(" ").trim() : "";
    const examIcon = preferences?.examIcon || "";
    const redirectionUrl = preferences?.redirection_url || data?.data?.redirect_url || "/";

    // Check if user has changed cohort in this session - if so, preserve their selection
    const cohortChangedInSession = sessionStorage.getItem('cohortChangedInSession') === 'true';

    if (preferences) {
      localStorage.setItem("preferences", JSON.stringify(preferences));
    }
    // Only update selectedExam* keys if user hasn't changed cohort in this session
    if (!cohortChangedInSession && (cohort || selectedExamName || examIcon)) {
      localStorage.setItem("selectedExamName", selectedExamName);
      localStorage.setItem("selectedExamIcon", examIcon);
      localStorage.setItem("selectedExamRedirectUrl", redirectionUrl);
    }

    localStorage.removeItem('pw-sync-token');
    localStorage.removeItem('pw-sync-random-id');
    sessionStorage.removeItem('force_logout');

    // Use replace instead of href for faster navigation (no history entry)
    window.location.replace(data?.data?.redirect_url);
    // Prioritize redirection_url from preferences, fallback to redirect_url from data
    //window.location.replace(redirectionUrl || "/");
  } catch (err) {
    clearTimeout(timeoutId);

    if (err.name === 'AbortError') {
      console.error("[PW Sync] API timeout after 10 seconds");
      sessionStorage.setItem('pw_auth_failed', 'true');
    } else {
      console.error("[PW Sync Error]", err);
    }

    forceLogout('Sync error: ' + err.message);
  }
}

/* -------------------- Token Context Logic -------------------- */

export function getTokenContext() {
  const params = new URLSearchParams(window.location.search);
  if (
    params.has("from_app") ||
    params.has("fromApp") ||
    params.has("from_App")
  ) {
    if (sessionStorage.getItem("fromApp") !== "true") {
      sessionStorage.setItem("fromApp", "true");
    }
  }

  const hash = window.location.hash;
  const redirectUrl = decodeURIComponent(params.get("redirect_url") || "/");
  const fullRedirectUrl = redirectUrl.includes('#') ? redirectUrl : `${redirectUrl}${hash}`;
  let filteredRedirectUrl = fullRedirectUrl.replace(/^https?:\/\/.*?\.live/, "");

  if (!filteredRedirectUrl.startsWith("/")) {
    filteredRedirectUrl = "/" + filteredRedirectUrl;
  }
  console.log("[PW Sync] Computed redirectUrl:", {
    raw: redirectUrl,
    hash,
    full: fullRedirectUrl,
    filtered: filteredRedirectUrl,
  });
  const storedContext = safeJSONParse(
    localStorage.getItem("STORE_TOKEN_CONTEXT")
  );
  const cookieContext = safeJSONParse(getCookie("TOKEN_CONTEXT"));

  return {
    stored: storedContext,
    cookie: cookieContext,
    live: {
      token: localStorage.getItem('pw-sync-token') || cookieContext?.token,
      randomId: localStorage.getItem('pw-sync-random-id') || cookieContext?.randomId,
    },
    redirectUrl: filteredRedirectUrl,
  };
}

export function shouldSkipSync({ storedContext, liveTokenContext }) {
  // Check if we recently synced (throttle) - 30s buffer
  const lastSyncTime = parseInt(sessionStorage.getItem('pw_last_sync_time') || '0', 10);
  if (Date.now() - lastSyncTime < 30000) {
      console.log("[PW Sync] Recently synced — skipping");
      return true;
  }

  if (!window.hasCustomer && liveTokenContext.token && liveTokenContext.randomId) {
    return false;
  }
  if (!liveTokenContext.token || !liveTokenContext.randomId) {
    console.log("[PW Sync] No valid token context found");
    return true;
  }

  if (
    storedContext?.randomId && storedContext?.token &&
    storedContext?.randomId === liveTokenContext?.randomId &&
    storedContext?.token === liveTokenContext?.token
  ) {
    console.log("[PW Sync] Token context unchanged — skipping sync");
    return true;
  }

  return false;
}
